import { m, AnimatePresence } from 'framer-motion';
import { AlertCircle, X } from 'lucide-react';
import React, { useState, useEffect, useRef } from 'react';

/**
 * ConfirmModal - Diálogo de confirmación premium con el branding de Grimmius Cattle Co.
 */
const ConfirmModal = ({ 
    isOpen, 
    onClose, 
    onConfirm, 
    title = "Are you sure?", 
    message = "This action cannot be undone.",
    confirmText = "CONFIRM",
    cancelText = "CANCEL",
    type = "warning", // 'warning' | 'danger' | 'info'
    validationCode,
    validationLabel = "ENTER CODE TO CONFIRM"
}) => {
    const [inputValue, setInputValue] = useState('');
    const inputRef = useRef(null);

    useEffect(() => {
        if (!isOpen) {
            setInputValue('');
        }
    }, [isOpen]);

    useEffect(() => {
        if (isOpen && validationCode && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isOpen, validationCode]);

    const isDisabled = Boolean(validationCode) && inputValue !== validationCode;

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-[999] flex items-center justify-center p-4">
                    {/* Backdrop con Blur */}
                    <m.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
                    />

                    {/* Modal Content */}
                    <m.div 
                        initial={{ scale: 0.9, opacity: 0, y: 20 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 0.9, opacity: 0, y: 20 }}
                        className="relative w-full max-w-md bg-white rounded-[2.5rem] shadow-2xl overflow-hidden border border-slate-100"
                    >
                        {/* Header Estilizado */}
                        <div className="p-8 pb-4 flex flex-col items-center text-center">
                            <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-6 shadow-inner ${
                                type === 'danger' ? 'bg-red-50 text-red-500' : 'bg-accent/10 text-accent'
                            }`}>
                                <AlertCircle size={32} strokeWidth={2.5} />
                            </div>
                            
                            <h3 className="text-2xl font-black text-primary tracking-tighter italic uppercase leading-tight mb-2">
                                {title}
                            </h3>
                            <div className="text-xs font-bold text-slate-400 uppercase tracking-widest leading-relaxed">
                                {typeof message === 'string'
                                    ? message.split('\n').map((line, i) => <p key={i}>{line}</p>)
                                    : message
                                }
                            </div>

                            {validationCode && (
                                <div className="mt-6 w-full flex flex-col items-center">
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                                        {validationLabel || 'ENTER CODE TO CONFIRM'}
                                        <span className="bg-slate-100 text-primary px-2 py-1 rounded text-sm font-black tracking-[0.2em] select-all">
                                            {validationCode}
                                        </span>
                                    </p>
                                    <input
                                        ref={inputRef}
                                        type="text"
                                        maxLength={4}
                                        value={inputValue}
                                        onChange={(e) => setInputValue(e.target.value.replace(/\D/g, '').slice(0, 4))}
                                        className="w-full text-center text-3xl font-black tracking-[0.3em] h-16 rounded-2xl border-2 border-slate-200 focus:border-accent focus:ring-0 outline-none transition-colors bg-slate-50 text-primary"
                                        placeholder="0000"
                                        autoComplete="off"
                                    />
                                </div>
                            )}
                        </div>

                        {/* Botones de Acción */}
                        <div className="p-8 pt-4 flex flex-col gap-3">
                            <button 
                                onClick={onConfirm}
                                disabled={isDisabled}
                                className={`w-full h-14 rounded-[1.2rem] font-black text-[11px] uppercase tracking-[0.2em] transition-all active:scale-95 shadow-xl ${
                                    type === 'danger' 
                                        ? 'bg-red-500 text-white shadow-red-500/20 hover:bg-red-600' 
                                        : 'bg-accent text-primary-dark shadow-accent/20 hover:bg-accent/90'
                                } ${isDisabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                            >
                                {confirmText}
                            </button>
                            <button 
                                onClick={onClose}
                                className="w-full h-14 rounded-[1.2rem] font-black text-[11px] uppercase tracking-[0.2em] text-slate-600 hover:text-primary transition-colors bg-white border-2 border-slate-200 hover:border-slate-300 hover:bg-slate-50 active:scale-95"
                            >
                                {cancelText}
                            </button>
                        </div>

                        {/* Botón Cerrar X (Esquina) */}
                        <button 
                            onClick={onClose}
                            className="absolute top-6 right-6 p-2 text-slate-300 hover:text-primary transition-colors hover:bg-slate-50 rounded-xl"
                        >
                            <X size={20} />
                        </button>
                    </m.div>
                </div>
            )}
        </AnimatePresence>
    );
};

export default ConfirmModal;
